import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Shield, Search, AlertTriangle, TrendingUp, Sparkles, ArrowRight, CheckCircle2, ScanSearch, BadgeCheck, Lock, Check, Crown } from "lucide-react";
import { useEffect, useState } from "react";
import { lenders } from "@/lib/mock-data";
import { Footer } from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "LendyTrack — Borrow Smarter. Avoid Predatory Loans." },
      {
        name: "description",
        content:
          "Search any lender, analyze loan offers, and detect predatory red flags before you borrow. AI-powered consumer financial protection.",
      },
      { property: "og:title", content: "LendyTrack — Consumer Loan Intelligence" },
      {
        property: "og:description",
        content: "Trust scores, sentiment analysis, and predatory lending detection for thousands of lenders.",
      },
      { property: "og:url", content: "/" },
      { property: "og:image", content: "/og-image.jpg" },
      { name: "twitter:image", content: "/og-image.jpg" },
      { name: "twitter:title", content: "LendyTrack — Consumer Loan Intelligence" },
      {
        name: "twitter:description",
        content: "Trust scores, sentiment analysis, and predatory lending detection for thousands of lenders.",
      },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
  component: Landing,
});

function Landing() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  // After Google OAuth returns to "/", forward to the saved destination
  // once the Supabase session is hydrated.
  useEffect(() => {
    let dest: string | null = null;
    try { dest = sessionStorage.getItem("lg_post_auth_redirect"); } catch {}
    if (!dest || !dest.startsWith("/") || dest.startsWith("//")) return;
    const target = dest;
    const go = () => {
      try { sessionStorage.removeItem("lg_post_auth_redirect"); } catch {}
      navigate({ to: target });
    };
    supabase.auth.getSession().then(({ data }) => { if (data.session) go(); });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === "SIGNED_IN" || event === "INITIAL_SESSION")) go();
    });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);


  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({ to: "/lenders", search: { q } });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-2 text-primary-foreground">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent/95 shadow-glow">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div className="font-display text-lg font-semibold tracking-tight">LendyTrack</div>
          </Link>
          <nav className="hidden items-center gap-8 text-sm text-primary-foreground/80 md:flex">
            <a href="#features" className="hover:text-primary-foreground">Features</a>
            <a href="#how" className="hover:text-primary-foreground">How it works</a>
            <a href="#pricing" className="hover:text-primary-foreground">Pricing</a>
            <a href="#trust" className="hover:text-primary-foreground">Trust</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/auth" className="text-sm font-medium text-primary-foreground/90 hover:text-primary-foreground">Sign in</Link>
            <Link
              to="/dashboard"
              className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground shadow-soft hover:opacity-90"
            >
              Open app
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-hero pb-28 pt-32 text-primary-foreground lg:pb-36 lg:pt-44">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, oklch(0.68 0.16 160 / 0.4), transparent 40%), radial-gradient(circle at 80% 30%, oklch(0.55 0.2 280 / 0.3), transparent 45%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-xs font-medium uppercase tracking-widest text-accent">
                <Sparkles className="h-3 w-3" /> AI-powered protection
              </span>
              <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
                Borrow Smarter.
                <br />
                <span className="bg-gradient-to-r from-accent to-[oklch(0.78_0.16_180)] bg-clip-text text-transparent">
                  Avoid Predatory Loans.
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-lg text-primary-foreground/75">
                AI-powered lender intelligence that helps you compare lenders, detect red flags,
                and make safer borrowing decisions — before you sign.
              </p>

              <form onSubmit={submit} className="mt-8 flex max-w-xl items-center gap-2 rounded-2xl border border-white/15 bg-white/10 p-2 backdrop-blur">
                <Search className="ml-3 h-5 w-5 text-primary-foreground/70" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search a lender (e.g. OppLoans, Upstart, SoFi)"
                  className="flex-1 bg-transparent px-2 py-2.5 text-sm text-primary-foreground placeholder:text-primary-foreground/50 focus:outline-none"
                />
                <button
                  type="submit"
                  className="rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground shadow-glow hover:opacity-90"
                >
                  Search
                </button>
              </form>

              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/analyzer"
                  className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/5 px-4 py-2 text-sm font-medium hover:bg-white/10"
                >
                  Analyze a loan offer <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-primary-foreground/80 hover:text-primary-foreground"
                >
                  View demo dashboard
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-primary-foreground/60">
                <div className="flex items-center gap-2"><Lock className="h-4 w-4" /> Bank-grade privacy</div>
                <div className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-accent" /> Independent scoring</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-accent" /> No data sold</div>
              </div>
            </div>

            {/* Hero card preview */}
            <div className="relative lg:pl-8">
              <div className="rounded-2xl border border-white/15 bg-card p-6 text-foreground shadow-elevated">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Lender Report</div>
                    <div className="mt-1 font-display text-2xl font-semibold">OppLoans</div>
                    <div className="text-xs text-muted-foreground">Installment Lender · Chicago, IL</div>
                  </div>
                  <span className="rounded-full bg-warning/20 px-3 py-1 text-xs font-semibold text-warning-foreground">
                    High Risk
                  </span>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[
                    { label: "Trust", v: 42, tone: "bg-warning" },
                    { label: "Predatory", v: 78, tone: "bg-danger" },
                    { label: "Sentiment", v: 38, tone: "bg-warning" },
                    { label: "Fee Clarity", v: 55, tone: "bg-accent" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg bg-muted p-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{s.label}</span>
                        <span className="font-semibold tabular-nums">{s.v}</span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-background">
                        <div className={`h-full ${s.tone}`} style={{ width: `${s.v}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-lg border border-danger/30 bg-danger/5 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 text-danger" />
                    <div className="text-xs">
                      <div className="font-semibold text-danger">RED FLAG</div>
                      <div className="text-muted-foreground">APR exceeds safe benchmark by 3.2x. 31% of negative reviews cite hidden fees.</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-4 hidden rounded-xl border border-white/15 bg-card p-4 shadow-elevated md:block">
                <div className="flex items-center gap-3">
                  <div className="grid h-9 w-9 place-items-center rounded-full bg-success/15 text-success"><CheckCircle2 className="h-5 w-5" /></div>
                  <div>
                    <div className="text-xs text-muted-foreground">Safer alternative</div>
                    <div className="text-sm font-semibold text-foreground">Upstart — 14.5% APR</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-24">
        <div className="max-w-2xl">
          <div className="text-xs font-medium uppercase tracking-widest text-accent">What you get</div>
          <h2 className="mt-2 font-display text-4xl font-semibold tracking-tight">A second opinion on every loan you consider.</h2>
          <p className="mt-3 text-muted-foreground">
            LendyTrack combines complaint data, sentiment analysis, and predatory-pattern detection
            into one independent score — so you know what you're signing.
          </p>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Search, title: "Lender Search Engine", body: "Trust, sentiment, complaints, and fee transparency for thousands of lenders." },
            { icon: AlertTriangle, title: "Predatory Detection", body: "AI flags high APR, balloon payments, hidden fees, and aggressive collections." },
            { icon: ScanSearch, title: "Loan Offer Analyzer", body: "Paste your offer. We surface true cost, red flags, and safer alternatives." },
            { icon: TrendingUp, title: "Financial Improvement", body: "A roadmap to raise approval odds and lower the rate you'd qualify for." },
            { icon: Sparkles, title: "AI Coach", body: "Plain-English answers about APR, fees, debt strategies, and lender risks." },
            { icon: Shield, title: "Consumer-First", body: "We don't get paid by lenders. Scores are independent and transparent." },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="group rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:shadow-elevated">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-gradient-accent text-accent-foreground">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-secondary py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs font-medium uppercase tracking-widest text-accent">How it works</div>
          <h2 className="mt-2 max-w-2xl font-display text-4xl font-semibold tracking-tight">
            Three steps to a safer borrowing decision.
          </h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Search the lender", d: "Type a name. Get an instant trust report with complaint trends and red flags." },
              { n: "02", t: "Analyze the offer", d: "Enter the amount, APR, and fees. We expose the true cost and find alternatives." },
              { n: "03", t: "Improve your odds", d: "Follow a step-by-step plan to qualify for better rates before you borrow." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                <div className="font-display text-5xl font-semibold text-accent">{s.n}</div>
                <div className="mt-3 font-display text-lg font-semibold">{s.t}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured lenders */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-medium uppercase tracking-widest text-accent">Sample reports</div>
            <h2 className="mt-2 font-display text-4xl font-semibold tracking-tight">
              Recently scored lenders
            </h2>
          </div>
          <Link to="/lenders" className="hidden text-sm font-medium text-primary hover:underline md:inline-flex">
            Browse all →
          </Link>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {lenders.slice(0, 6).map((l) => (
            <Link
              key={l.slug}
              to="/lenders/$slug"
              params={{ slug: l.slug }}
              className="group rounded-2xl border border-border bg-card p-5 shadow-soft transition hover:-translate-y-0.5 hover:shadow-elevated"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-semibold">{l.name}</div>
                  <div className="text-xs text-muted-foreground">{l.category}</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${
                  l.recommendation === "safe" ? "bg-success/15 text-success" :
                  l.recommendation === "compare" ? "bg-accent/15 text-accent-foreground" :
                  l.recommendation === "high-risk" ? "bg-warning/20 text-warning-foreground" :
                  "bg-danger/15 text-danger"
                }`}>
                  {l.recommendation === "safe" ? "Safe" : l.recommendation === "compare" ? "Compare" : l.recommendation === "high-risk" ? "High Risk" : "Avoid"}
                </span>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-md bg-muted py-2">
                  <div className="text-muted-foreground">Trust</div>
                  <div className="font-semibold tabular-nums">{l.scores.trust}</div>
                </div>
                <div className="rounded-md bg-muted py-2">
                  <div className="text-muted-foreground">APR</div>
                  <div className="font-semibold tabular-nums">{l.aprRange[0]}–{l.aprRange[1]}%</div>
                </div>
                <div className="rounded-md bg-muted py-2">
                  <div className="text-muted-foreground">Sentiment</div>
                  <div className="font-semibold tabular-nums">{l.scores.sentiment}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trust / testimonials */}
      <section id="trust" className="bg-gradient-hero py-24 text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <div className="text-xs font-medium uppercase tracking-widest text-accent">Why people trust LendyTrack</div>
              <h2 className="mt-2 font-display text-4xl font-semibold tracking-tight">Independent. Consumer-funded. No lender kickbacks.</h2>
              <p className="mt-4 text-primary-foreground/75">
                Most loan comparison sites are paid by the lenders they rank.
                LendyTrack's scores can't be bought. We surface the data lenders
                hope you never read.
              </p>
              <div className="mt-8 grid grid-cols-3 gap-6">
                {[
                  { k: "3.2M", v: "Loans analyzed" },
                  { k: "180k", v: "Red flags detected" },
                  { k: "$412", v: "Avg. saved per user" },
                ].map((s) => (
                  <div key={s.v}>
                    <div className="font-display text-3xl font-semibold">{s.k}</div>
                    <div className="text-xs text-primary-foreground/60">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              {[
                { q: "Found out the loan I almost signed had a 187% APR. Switched lenders and saved $2,400.", a: "Renee P." },
                { q: "The financial health plan got me from 590 to 672 before I applied. I qualified for prime.", a: "Marcus J." },
                { q: "The AI coach actually answered my questions in plain English. No upsells.", a: "Priya S." },
              ].map((t) => (
                <div key={t.a} className="rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur">
                  <p className="text-sm">"{t.q}"</p>
                  <div className="mt-3 text-xs text-primary-foreground/60">— {t.a}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-secondary py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <div className="text-xs font-medium uppercase tracking-widest text-accent">Pricing</div>
            <h2 className="mt-2 font-display text-4xl font-semibold tracking-tight">
              Protection that pays for itself.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Start free. Upgrade when you want unlimited analyses, deeper lender intelligence,
              and your personal AI coach on call 24/7.
            </p>
          </div>

          <div className="mx-auto mt-14 grid max-w-5xl gap-6 md:grid-cols-2">
            {/* Free tier */}
            <div className="flex flex-col rounded-2xl border border-border bg-card p-8 shadow-soft">
              <div className="flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-foreground">
                  <Shield className="h-5 w-5" />
                </div>
                <div className="font-display text-xl font-semibold">Free</div>
              </div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display text-5xl font-semibold tracking-tight">$0</span>
                <span className="text-sm text-muted-foreground">/ forever</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                Essential consumer protection for occasional borrowers.
              </p>

              <ul className="mt-6 space-y-3 text-sm">
                {[
                  "Lender search with trust scores",
                  "3 loan analyses per month",
                  "Basic predatory red-flag detection",
                  "Public complaint & sentiment data",
                  "AI Coach: 10 messages / month",
                  "Save up to 5 lender comparisons",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/auth"
                className="mt-8 inline-flex items-center justify-center rounded-md border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground hover:bg-muted"
              >
                Start free
              </Link>
            </div>

            {/* Premium tier */}
            <div className="relative flex flex-col overflow-hidden rounded-2xl border border-accent/40 bg-gradient-hero p-8 text-primary-foreground shadow-elevated">
              <div
                className="pointer-events-none absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 80% 0%, oklch(0.68 0.16 160 / 0.5), transparent 50%)",
                }}
              />
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-accent text-accent-foreground">
                      <Crown className="h-5 w-5" />
                    </div>
                    <div className="font-display text-xl font-semibold">Premium</div>
                  </div>
                  <span className="rounded-full bg-accent px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-accent-foreground">
                    Most popular
                  </span>
                </div>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-semibold tracking-tight">$12</span>
                  <span className="text-sm text-primary-foreground/70">/ month</span>
                </div>
                <p className="mt-2 text-sm text-primary-foreground/75">
                  Full intelligence stack for active borrowers and rate shoppers.
                </p>

                <ul className="mt-6 space-y-3 text-sm">
                  {[
                    "Everything in Free",
                    "Unlimited loan offer analyses",
                    "Advanced predatory pattern detection",
                    "Side-by-side lender comparisons",
                    "Personalized financial health roadmap",
                    "Unlimited AI Coach with saved threads",
                    "Rate-drop & lender alert monitoring",
                    "Priority support & dispute templates",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/auth"
                  className="mt-8 inline-flex items-center justify-center rounded-md bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-glow hover:opacity-90"
                >
                  Upgrade to Premium
                </Link>
                <div className="mt-3 text-center text-xs text-primary-foreground/60">
                  14-day money-back guarantee · Cancel anytime
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-8 max-w-2xl text-center text-xs text-muted-foreground">
            Need a team or enterprise plan for a credit union or nonprofit? <a href="#" className="font-medium text-foreground underline">Contact us</a>.
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-6 py-24 text-center">
        <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          Don't sign that loan blind.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
          One free check could save you thousands. Start with a lender search or
          drop your loan offer in the analyzer.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/lenders" className="rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-soft hover:opacity-90">
            Search a Lender
          </Link>
          <Link to="/analyzer" className="rounded-md bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-soft hover:opacity-90">
            Analyze Loan Offer
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
